// API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Create a new shared result
 * @param {Object} sharedResult - The shared result data
 * @param {string} sharedResult.query - The query string
 * @param {Array} sharedResult.outputs - Array of output objects
 * @param {string} accessToken - Access token for authorization
 * @returns {Promise<Object>} The created shared result
 */
export const createSharedResult = async (sharedResult, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/api/shared-results`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(sharedResult)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = 
      (errorData && errorData.detail) 
        ? errorData.detail 
        : `Failed to create shared result: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Get paginated list of shared results
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Results per page (default: 20)
 * @param {string} params.sort_by - Sort field (default: 'like_count')
 * @param {string} accessToken - Access token for authorization
 * @returns {Promise<Object>} Paginated shared results
 */
export const getSharedResults = async (params = {}, accessToken) => {
  const queryParams = new URLSearchParams({
    page: params.page || 1,
    limit: params.limit || 20,
    sort_by: params.sort_by || 'like_count'
  });

  const response = await fetch(`${API_BASE_URL}/api/shared-results?${queryParams}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = 
      (errorData && errorData.detail) 
        ? errorData.detail 
        : `Failed to get shared results: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Get a specific shared result by ID
 * @param {number} resultId - The shared result ID
 * @param {string} accessToken - Access token for authorization
 * @returns {Promise<Object>} The shared result with outputs
 */
export const getSharedResultById = async (resultId, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/api/shared-results/${resultId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = 
      (errorData && errorData.detail) 
        ? errorData.detail 
        : `Failed to get shared result: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Toggle like status for a shared result
 * @param {number} resultId - The shared result ID
 * @param {string} accessToken - Access token for authorization
 * @returns {Promise<Object>} The updated like status
 */
export const toggleLike = async (resultId, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/api/shared-results/${resultId}/like`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = 
      (errorData && errorData.detail) 
        ? errorData.detail 
        : `Failed to toggle like: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Delete a shared result (owner only)
 * @param {number} resultId - The shared result ID
 * @param {string} accessToken - Access token for authorization
 * @returns {Promise<void>}
 */
export const deleteSharedResult = async (resultId, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/api/shared-results/${resultId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = 
      (errorData && errorData.detail) 
        ? errorData.detail 
        : `Failed to delete shared result: ${response.status}`;
    throw new Error(errorMessage);
  }
}; 